import React from "react";
import { Link } from "react-router-dom";
import { Bicycle, PersonSimpleRun, TennisBall, ArrowRight, MapTrifold, Users, ChatCircleText, Sun, Moon, Translate, DeviceMobile, MapPin, Lightning, Broadcast } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import AppStoreButtons from "@/components/AppStoreButtons";

export default function Landing() {
  const { user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <div className="bg-grid absolute inset-0 opacity-50 pointer-events-none" />
      <header className="relative tf-glass">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#007AFF] flex items-center justify-center">
              <span className="font-display text-sm">TF</span>
            </div>
            <span className="font-display text-xl tracking-tight">TEAMFIT</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="landing-lang-toggle" onClick={() => setLang(lang === "da" ? "en" : "da")} className="px-2 py-2 hover:bg-white/5 flex items-center gap-1 text-xs uppercase tracking-widest font-bold">
              <Translate weight="duotone" size={16} /> {lang.toUpperCase()}
            </button>
            <button data-testid="landing-theme-toggle" onClick={toggle} className="p-2 hover:bg-white/5">
              {theme === "dark" ? <Sun weight="duotone" size={18} /> : <Moon weight="duotone" size={18} />}
            </button>
            {user ? (
              <Link to="/dashboard" data-testid="landing-dashboard-btn" className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-4 py-2 text-sm font-bold uppercase tracking-widest">{t("landing.cta_open")}</Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-link" className="text-sm text-zinc-300 hover:text-white px-3 py-2">{t("common.login")}</Link>
                <Link to="/register" data-testid="landing-register-btn" className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-4 py-2 text-sm font-bold uppercase tracking-widest">{t("landing.get_started")}</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative max-w-7xl mx-auto px-6 pt-20 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="label-overline text-[#007AFF] mb-4">{t("landing.kicker")}</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              {t("landing.hero1")}<br />
              <span className="text-[#007AFF]">{t("landing.hero2")}</span><br />
              {t("landing.hero3")}
            </h1>
            <p className="mt-6 text-zinc-400 max-w-xl text-base sm:text-lg">{t("landing.intro")}</p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link to="/register" data-testid="hero-cta-register" className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-6 py-3 font-bold uppercase tracking-widest text-sm flex items-center gap-2">
                {t("landing.cta_register")} <ArrowRight weight="bold" size={16} />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="kinetic-btn border border-[#262626] hover:border-white/40 text-white px-6 py-3 font-bold uppercase tracking-widest text-sm">
                {t("landing.cta_login")}
              </Link>
            </div>
            <div className="mt-6">
              <div className="label-overline text-zinc-500 mb-3 flex items-center gap-2">
                <DeviceMobile weight="duotone" size={14} />
                {t("landing.download_kicker")}
              </div>
              <AppStoreButtons size="md" />
            </div>
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
              <Pill icon={<Bicycle weight="duotone" size={20} color="#007AFF" />} label="Cycling" />
              <Pill icon={<PersonSimpleRun weight="duotone" size={20} color="#FF3B30" />} label="Running" />
              <Pill icon={<TennisBall weight="duotone" size={20} color="#34C759" />} label="Padel" />
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up">
            <div className="relative">
              <div className="absolute -inset-2 bg-[#007AFF]/10 blur-2xl pointer-events-none" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/44618a01-ff58-445c-83e6-81e80fc4fb8c/images/040f357a6893a755abff9ccf828a6b880d39ae030c60cf074f2e79bd5249bf14.png"
                alt="Athlete in motion"
                className="relative w-full max-h-[520px] object-cover border border-[#262626]"
              />
              <div className="absolute bottom-4 left-4 right-4 tf-glass p-4 flex items-center justify-between">
                <div>
                  <div className="label-overline">{t("landing.live_now")}</div>
                  <div className="font-display text-xl mt-1">Copenhagen → Roskilde</div>
                </div>
                <div className="text-right">
                  <div className="label-overline">{t("common.spots")}</div>
                  <div className="font-display text-2xl">4/6</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-t border-[#262626]" data-testid="download-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="label-overline text-[#007AFF] mb-3 flex items-center gap-2">
                <DeviceMobile weight="duotone" size={14} />
                {t("landing.download_section_kicker")}
              </div>
              <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight">
                {t("landing.download_section_title")}
              </h2>
              <p className="mt-5 text-zinc-400 text-base sm:text-lg max-w-xl">
                {t("landing.download_section_body")}
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
                <Perk icon={<MapPin weight="duotone" size={20} color="#007AFF" />} text={t("landing.download_perk1")} />
                <Perk icon={<Broadcast weight="duotone" size={20} color="#FF3B30" />} text={t("landing.download_perk2")} />
                <Perk icon={<Lightning weight="duotone" size={20} color="#FFCC00" />} text={t("landing.download_perk3")} />
                <Perk icon={<Users weight="duotone" size={20} color="#34C759" />} text={t("landing.download_perk4")} />
              </div>

              <div className="mt-8">
                <AppStoreButtons size="lg" />
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <PhoneMockup t={t} lang={lang} />
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-6">
            <Feature icon={<MapTrifold weight="duotone" size={28} color="#007AFF" />} title={t("landing.feature1_title")} text={t("landing.feature1_text")} />
            <Feature icon={<Users weight="duotone" size={28} color="#FF3B30" />} title={t("landing.feature2_title")} text={t("landing.feature2_text")} />
            <Feature icon={<ChatCircleText weight="duotone" size={28} color="#34C759" />} title={t("landing.feature3_title")} text={t("landing.feature3_text")} />
          </div>
        </div>
      </section>
    </div>
  );
}

function Pill({ icon, label }) {
  return (
    <div className="flex items-center gap-2 border border-[#262626] bg-[#141414] px-3 py-2">
      {icon}
      <span className="text-xs uppercase tracking-widest">{label}</span>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="tf-card p-6">
      <div className="w-12 h-12 flex items-center justify-center border border-[#262626]">{icon}</div>
      <h3 className="font-display text-2xl mt-4">{title}</h3>
      <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{text}</p>
    </div>
  );
}

function Perk({ icon, text }) {
  return (
    <div className="flex items-center gap-3 border border-[#262626] bg-[#141414] px-4 py-3">
      <div className="shrink-0">{icon}</div>
      <span className="text-sm leading-snug">{text}</span>
    </div>
  );
}

/**
 * Phone-frame mockup illustrating the mobile app. Purely decorative — uses
 * the same hero image inside a phone bezel so the marketing surface always
 * tells the same story as the actual product.
 */
function PhoneMockup({ t, lang }) {
  return (
    <div className="relative mx-auto" style={{ maxWidth: 320 }} data-testid="phone-mockup">
      <div className="absolute -inset-6 bg-[#007AFF]/10 blur-3xl pointer-events-none" aria-hidden />
      <div
        className="relative aspect-[9/19] bg-black border-[8px] border-[#1A1A1A] rounded-[2.5rem] overflow-hidden shadow-2xl"
        style={{ boxShadow: "0 30px 80px rgba(0,122,255,0.25)" }}
      >
        {/* notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-b-2xl z-20" aria-hidden />
        {/* status bar */}
        <div className="absolute top-2 left-0 right-0 flex items-center justify-between px-7 text-[10px] font-mono-tag text-white z-30">
          <span>09:41</span>
          <span>·····</span>
        </div>
        {/* hero image */}
        <img
          src="https://static.prod-images.emergentagent.com/jobs/44618a01-ff58-445c-83e6-81e80fc4fb8c/images/040f357a6893a755abff9ccf828a6b880d39ae030c60cf074f2e79bd5249bf14.png"
          alt="TeamFit on mobile"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* overlay card mimicking an activity row */}
        <div className="absolute bottom-4 left-3 right-3 backdrop-blur-md bg-black/60 border border-white/15 p-3 z-10">
          <div className="label-overline text-[#34C759] text-[9px]">{t("landing.live_now")}</div>
          <div className="font-display text-base leading-tight mt-1">Copenhagen → Roskilde</div>
          <div className="mt-2 flex items-center justify-between text-[10px] text-white/80 font-mono-tag">
            <span>32.4 KM · 75 MIN</span>
            <span>4/6 {lang === "da" ? "PLADSER" : "SPOTS"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
